import type { PharmacyOnboardFormData } from '@/components/onboarding/OnboardingForm';
import { useGetPharmacyOnboarding } from '@/hooks/onboarding/useGetPharmacyOnboarding';
import { Logger } from '@/utils/logger';
import { getTsPharmacyClient } from '@montugroup/circuit-shared/api/pharmacy.client';
import { buildOpeningHours, handleOnboardingErrorResponse } from '@montugroup/circuit-shared/utils/pharmacyOnboarding';
import { Button, toast } from '@montugroup/design-system';
import { Box, styled } from '@mui/material';
import type { ClientInferRequest } from '@ts-rest/core';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

type NextStepButtonProps = {
  text: string;
  path: string;
};

const ButtonContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const logger = new Logger('PharmacyOnboarding - NextStepButton');
const client = getTsPharmacyClient();

function SubmitOnboardingFormButton({ path, text }: NextStepButtonProps) {
  const { token } = useParams();
  const { data } = useGetPharmacyOnboarding({ token });
  const navigate = useNavigate();
  const { handleSubmit, formState } = useFormContext<PharmacyOnboardFormData>();

  const submitPharmacyOnboardMutation = client.updatePharmacyOnboarding.useMutation({
    onSuccess: () => {
      navigate(`/onboard/${token}/${path}`);
    },
    onError: handleOnboardingErrorResponse,
    meta: {
      error: {
        log: 'Update pharmacy onboarding failed',
      },
    },
  });

  const mapFormDataToPayload = (
    formData: PharmacyOnboardFormData,
  ): ClientInferRequest<typeof submitPharmacyOnboardMutation.contractEndpoint>['body']['data'] | void => {
    let address = formData.fullAddress.addressLine1;
    if (formData.fullAddress.addressLine2) {
      address = `${address}$$$$${formData.fullAddress.addressLine2}`;
    }

    let businessContactPerson = formData.proprietor.firstName;
    if (formData.proprietor.lastName) {
      // todo update when BE updates to $$$$ delimiter
      // todo also handle last name but no first name
      businessContactPerson = `${businessContactPerson}$$$$${formData.proprietor.lastName}`;
    }

    let pharmacistName = formData.pharmacist.firstName;
    if (formData.pharmacist.lastName) {
      // todo same as proprietor above
      pharmacistName = `${pharmacistName}$$$$${formData.pharmacist.lastName}`;
    }

    return {
      abn: formData.abn,
      acceptsMarketing: formData.emailPreferences.subscribe,
      address,
      businessContactEmail: formData.proprietor.email,
      businessContactPerson: businessContactPerson,
      businessContactPhone: formData.proprietor.phone,
      city: formData.fullAddress.suburb,
      dayToDayEmail: formData.emailPreferences.communicationEmail ?? '',
      financeEmail: formData.emailPreferences.financialEmail ?? '',
      openingHours: buildOpeningHours({
        pharmacyId: data?.body.data.id ?? null,
        operatingHours: formData.operatingHours,
      }),
      pharmacistAhpraNumber: formData.proprietor.ahpraNumber,
      pharmacistName,
      pharmacyName: formData.pharmacyTradingName,
      state: formData.fullAddress.state,
      termsAndConditionsAgreed: true,
      pharmacistPhone: formData.pharmacist.phone,
      zipCode: formData.fullAddress.postcode,
    };
  };

  const submitForm = (formData: PharmacyOnboardFormData) => {
    const payload = mapFormDataToPayload(formData);
    if (payload) {
      submitPharmacyOnboardMutation.mutate({
        body: { data: payload },
        params: {
          token: token ?? '',
        },
      });
    }
  };

  return (
    <ButtonContainer>
      <Button
        disabled={!data}
        variant="contained"
        size="large"
        type="submit"
        sx={{ marginTop: 8 }}
        onClick={handleSubmit(submitForm, () => {
          toast.error('Please complete all fields before continuing.');
          logger.error(`Invalid form`, formState);
        })}
      >
        {text}
      </Button>
    </ButtonContainer>
  );
}

export default SubmitOnboardingFormButton;
